import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { 
  Download, 
  Play, 
  Pause, 
  CheckCircle, 
  Clock, 
  Target, 
  Dumbbell,
  Calendar,
  X
} from 'lucide-react';
import { WorkoutProgram } from '@/hooks/useWorkoutPrograms';
import { exportToPDF } from '@/lib/pdfExport';
import { toast } from 'sonner';
// convertToWorkoutLoggerFormat is no longer used - direct conversion in handleStartWorkout
import { Analytics } from '@/lib/analytics';

interface WorkoutProgramViewerProps {
  program: WorkoutProgram | null;
  isOpen: boolean;
  onClose: () => void;
  onStatusUpdate: (programId: string, status: string) => void;
  onDayComplete: (programId: string, dayId: string, dayData: any) => Promise<any>;
}

export const WorkoutProgramViewer = ({ 
  program, 
  isOpen, 
  onClose, 
  onStatusUpdate,
  onDayComplete 
}: WorkoutProgramViewerProps) => {
  const [isExporting, setIsExporting] = useState(false);
  const [completingDay, setCompletingDay] = useState<string | null>(null);
  const navigate = useNavigate();

  if (!program) return null;

  const isRunningWorkout = (exerciseName: string) => {
    return exerciseName.toLowerCase().includes('running');
  };

  const cleanLabel = (name: string) => name.replace(/\s*\(Dedicated\)/i, '').trim();

  const isWorkoutDayRunning = (exercises: any[]) => {
    return exercises.length === 1 && isRunningWorkout(exercises[0].exercise_name);
  };
  const handleStartWorkout = (day: any) => {
    try {
      console.log('Starting workout from program day:', day);
      console.log('Day exercises:', day.exercises);
      
      // Build exercises directly for the gym workout format
      // Bypass convertToWorkoutLoggerFormat which has strict filtering
      const exercises = (day.exercises || []).map((ex: any, index: number) => {
        // Handle both reps (single value) and reps_min/reps_max (range)
        let repsValue = 10; // Default fallback
        if (ex.reps_max !== null && ex.reps_max !== undefined) {
          repsValue = typeof ex.reps_max === 'number' ? ex.reps_max : parseInt(String(ex.reps_max), 10) || 10;
        } else if (ex.reps_min !== null && ex.reps_min !== undefined) {
          repsValue = typeof ex.reps_min === 'number' ? ex.reps_min : parseInt(String(ex.reps_min), 10) || 10;
        } else if (ex.reps !== null && ex.reps !== undefined) {
          repsValue = typeof ex.reps === 'number' ? ex.reps : parseInt(String(ex.reps), 10) || 10;
        }

        // Parse sets with fallback
        let setsValue = 3; // Default fallback
        if (ex.sets !== null && ex.sets !== undefined) {
          setsValue = typeof ex.sets === 'number' ? ex.sets : parseInt(String(ex.sets), 10) || 3;
        }
        
        // Parse weight with fallback to 0 (bodyweight)
        let weightValue = 0;
        if (ex.weight_kg !== null && ex.weight_kg !== undefined) {
          weightValue = typeof ex.weight_kg === 'number' ? ex.weight_kg : parseFloat(String(ex.weight_kg)) || 0;
        }

        const exerciseData = {
          id: `program-import-${Date.now()}-${index}`,
          name: String(ex.exercise_name || 'Unknown Exercise').trim(),
          sets: setsValue,
          reps: repsValue,
          weight: weightValue,
          muscleGroups: ex.muscle_groups || [],
          notes: ex.notes || null,
          matched: true // Mark as matched so GymWorkout handles it properly
        };
        
        console.log('Converted exercise:', exerciseData);
        return exerciseData;
      });

      console.log('Final exercises for import:', exercises);

      if (exercises.length > 0) {
        localStorage.removeItem('activeWorkoutSession');
        localStorage.setItem('importedWorkout', JSON.stringify({
          title: day.workout_title || 'Program Day',
          exercises,
          imported: true,
        }));
        console.log('Saved importedWorkout to localStorage');
      } else {
        console.warn('No exercises to import from program day');
      }

      if (isWorkoutDayRunning(day.exercises)) {
        navigate('/running-workout');
        toast.success('Opening running workout tracker');
      } else {
        navigate('/gym-workout');
        toast.success('Opening gym workout tracker');
      }
    } catch (e) {
      console.error('Failed to start workout from program day:', e);
      navigate('/gym-workout');
    }
    onClose();
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      await exportToPDF(program);
      toast.success('Workout program exported to PDF successfully!');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Failed to export PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleStatusUpdate = async (status: string) => {
    try {
      await onStatusUpdate(program.id, status);
      toast.success(`Program ${status} successfully!`);
    } catch (error) {
      toast.error('Failed to update program status');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'completed': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'paused': return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
      default: return 'bg-muted/10 text-muted-foreground border-border';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <Play className="h-3 w-3" />;
      case 'completed': return <CheckCircle className="h-3 w-3" />;
      case 'paused': return <Pause className="h-3 w-3" />;
      default: return <Clock className="h-3 w-3" />;
    }
  };

  const handleDayComplete = async (dayId: string, dayData: any) => {
    setCompletingDay(dayId);
    try {
      await onDayComplete(program.id, dayId, dayData);
      toast.success('Workout day marked as complete! 🎉');
    } catch (error) {
      toast.error('Failed to mark day as complete');
    } finally {
      setCompletingDay(null);
    }
  };

  const getCompletedCount = () => {
    return program.days.filter(d => d.completed_at).length;
  };

  const getProgressPercentage = () => {
    return Math.round((getCompletedCount() / program.days.length) * 100);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-full max-w-[420px] sm:max-w-3xl max-h-[90vh] p-0 mx-auto flex flex-col overflow-hidden">
        <div
          data-program-scroll
          className="program-scroll-touch flex-1 min-h-0 overflow-y-auto overflow-x-hidden"
          style={{
            WebkitOverflowScrolling: 'touch',
            touchAction: 'pan-y',
            overscrollBehavior: 'contain',
          }}
        >
          <div className="p-6 pb-4">
            <DialogHeader className="pb-0">
              <div className="flex items-center justify-between pr-12">
                <div className="flex-1">
                  <DialogTitle className="text-2xl font-bold mb-2">
                    {program.title}
                  </DialogTitle>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {program.duration_weeks} weeks
                    </div>
                    <div className="flex items-center gap-1">
                      <Target className="h-4 w-4" />
                      {getCompletedCount()}/{program.days.length} completed ({getProgressPercentage()}%)
                    </div>
                    <Badge className={getStatusColor(program.status)}>
                      {getStatusIcon(program.status)}
                      {program.status}
                    </Badge>
                  </div>
                </div>
              </div>
            </DialogHeader>

            <div className="mt-4">
              <div className="flex flex-wrap justify-center sm:justify-start gap-2 mb-4">
                <Button
                  onClick={handleExportPDF}
                  disabled={isExporting}
                  className="bg-gradient-primary text-black text-sm"
                  size="sm"
                >
                  <Download className="h-3 w-3 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">{isExporting ? 'Exporting...' : 'Export PDF'}</span>
                  <span className="sm:hidden">PDF</span>
                </Button>

                {program.status !== 'active' && (
                  <Button
                  onClick={() => handleStatusUpdate('active')}
                  variant="outline"
                  size="sm"
                  className="text-sm"
                >
                  <Play className="h-3 w-3 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Start Program</span>
                  <span className="sm:hidden">Start</span>
                </Button>
                )}

                {program.status === 'active' && (
                  <Button
                    onClick={() => handleStatusUpdate('paused')}
                    variant="outline"
                    size="sm"
                    className="text-sm"
                  >
                    <Pause className="h-3 w-3 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Pause Program</span>
                    <span className="sm:hidden">Pause</span>
                  </Button>
                )}

                {(program.status === 'active' || program.status === 'paused') && (
                  <Button
                    onClick={() => handleStatusUpdate('completed')}
                    variant="outline"
                    size="sm"
                    className="text-sm"
                  >
                    <CheckCircle className="h-3 w-3 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Mark Complete</span>
                    <span className="sm:hidden">Complete</span>
                  </Button>
                )}
              </div>

              {program.description && (
                <Card className="mb-4">
                  <CardContent className="pt-4">
                    <p className="text-muted-foreground">{program.description}</p>
                  </CardContent>
                </Card>
              )}
            </div>

            <Tabs defaultValue="overview" className="mt-4">
              <div className="px-4 sm:px-6">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="overview" className="text-xs sm:text-sm">Overview</TabsTrigger>
                  <TabsTrigger value="detailed" className="text-xs sm:text-sm">Detailed View</TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="overview" className="px-4 sm:px-6 mt-4">
                <div className="grid gap-4 pb-6">
                {program.days.map((day) => (
                  <Card key={day.id} className={`border-l-4 ${day.completed_at ? 'border-l-green-500 bg-green-500/5' : 'border-l-primary'}`}>
                    <CardHeader className="pb-3">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <CardTitle className="text-base sm:text-lg">
                            {day.day_name || `Day ${day.day_number}`}: {cleanLabel(day.workout_title)}
                          </CardTitle>
                          {day.completed_at && (
                            <Badge className="bg-green-500/10 text-green-500 border-green-500/20">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Done
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          {day.estimated_duration_minutes || 45} min
                        </div>
                      </div>
                      {day.workout_description && (
                        <p className="text-sm text-muted-foreground">
                          {day.workout_description}
                        </p>
                      )}
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="flex flex-col gap-3">
                        {isWorkoutDayRunning(day.exercises) ? (
                          <div className="bg-gradient-to-br from-primary/5 to-primary/10 p-4 rounded-lg">
                            <div className="flex items-center gap-2 mb-2">
                              <Play className="h-4 w-4 text-primary" />
                              <span className="font-medium text-primary">Running Workout</span>
                            </div>
                            {day.exercises[0].notes ? (
                              <p className="text-sm text-foreground whitespace-pre-wrap">
                                {day.exercises[0].notes}
                              </p>
                            ) : (
                              <p className="text-sm text-muted-foreground">
                                Running session
                              </p>
                            )}
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {day.exercises.map((exercise) => (
                              <Badge key={exercise.id} variant="secondary" className="text-xs">
                                <Dumbbell className="h-3 w-3 mr-1" />
                                {cleanLabel(exercise.exercise_name)}
                              </Badge>
                            ))}
                          </div>
                        )}
                        <div className="flex flex-col items-stretch sm:flex-row sm:justify-center gap-2 sm:gap-3">
                          <Button 
                            size="sm" 
                            onClick={() => handleStartWorkout(day)}
                            className="bg-gradient-primary text-black sm:flex-1"
                            disabled={!!day.completed_at}
                          >
                            <Play className="h-3 w-3 mr-2" />
                            Start {isWorkoutDayRunning(day.exercises) ? 'Run' : 'Workout'}
                          </Button>
                          {!day.completed_at && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleDayComplete(day.id, day)}
                              disabled={completingDay === day.id}
                              className="border-green-500 text-green-500 hover:bg-green-500/10 sm:flex-1"
                            >
                              <CheckCircle className="h-3 w-3 mr-2" />
                              {completingDay === day.id ? 'Marking...' : 'Mark Done'}
                            </Button>
                          )}
                        </div>

                      </div>
                    </CardContent>
                  </Card>
                ))}
                </div>
              </TabsContent>

              <TabsContent value="detailed" className="px-4 sm:px-6 mt-4">
                <div className="space-y-6 pb-6">
                {program.days.map((day, dayIndex) => (
                  <div key={day.id}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-8 h-8 ${day.completed_at ? 'bg-green-500 text-white' : 'bg-gradient-primary text-black'} rounded-full flex items-center justify-center font-bold text-sm`}>
                        {day.completed_at ? <CheckCircle className="h-5 w-5" /> : day.day_number}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-lg">
                            {day.day_name || `Day ${day.day_number}`}
                          </h3>
                          {day.completed_at && (
                            <Badge className="bg-green-500/10 text-green-500 border-green-500/20">
                              Completed
                            </Badge>
                          )}
                        </div>
                        <p className="text-muted-foreground">{cleanLabel(day.workout_title)}</p>
                      </div>
                    </div>

                    <div className="ml-11 space-y-3">
                      {isWorkoutDayRunning(day.exercises) ? (
                        <Card className="p-4 bg-gradient-to-br from-primary/5 to-primary/10">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 bg-gradient-primary rounded-full flex items-center justify-center">
                              <Play className="h-5 w-5 text-black" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-lg">Running Workout</h4>
                              <p className="text-sm text-muted-foreground">Running session</p>
                            </div>
                          </div>
                          {day.exercises[0].notes ? (
                            <div className="p-3 bg-background/80 rounded-md text-sm whitespace-pre-wrap">
                              {day.exercises[0].notes}
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground">
                              No specific workout details provided
                            </p>
                          )}
                          <div className="mt-4 flex gap-2">
                            <Button 
                              onClick={() => handleStartWorkout(day)}
                              className="bg-gradient-primary text-black flex-1"
                              disabled={!!day.completed_at}
                            >
                              <Play className="h-4 w-4 mr-2" />
                              Start Running Workout
                            </Button>
                            {!day.completed_at && (
                              <Button 
                                variant="outline"
                                onClick={() => handleDayComplete(day.id, day)}
                                disabled={completingDay === day.id}
                                className="border-green-500 text-green-500 hover:bg-green-500/10"
                              >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                {completingDay === day.id ? 'Marking...' : 'Done'}
                              </Button>
                            )}
                          </div>
                        </Card>
                      ) : (
                        <>
                          {day.exercises.map((exercise, index) => (
                            <Card key={exercise.id} className="p-4">
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="font-medium">{cleanLabel(exercise.exercise_name)}</h4>
                                <Badge variant="outline" className="text-xs">
                                  Exercise {index + 1}
                                </Badge>
                              </div>
                              
                              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 text-sm">
                                {exercise.sets && (
                                  <div>
                                    <span className="text-muted-foreground">Sets:</span>
                                    <p className="font-medium">{exercise.sets}</p>
                                  </div>
                                )}
                                {(exercise.reps_min || exercise.reps_max) && (
                                  <div>
                                    <span className="text-muted-foreground">Reps:</span>
                                    <p className="font-medium">
                                      {exercise.reps_min === exercise.reps_max 
                                        ? exercise.reps_min 
                                        : `${exercise.reps_min}-${exercise.reps_max}`
                                      }
                                    </p>
                                  </div>
                                )}
                                {exercise.weight_kg && (
                                  <div>
                                    <span className="text-muted-foreground">Weight:</span>
                                    <p className="font-medium">{exercise.weight_kg}kg</p>
                                  </div>
                                )}
                                {exercise.rest_seconds && (
                                  <div>
                                    <span className="text-muted-foreground">Rest:</span>
                                    <p className="font-medium">{exercise.rest_seconds}s</p>
                                  </div>
                                )}
                              </div>

                              {exercise.muscle_groups && exercise.muscle_groups.length > 0 && (
                                <div className="mt-3">
                                  <div className="flex flex-wrap gap-1">
                                    {exercise.muscle_groups.map((muscle) => (
                                      <Badge key={muscle} variant="outline" className="text-xs">
                                        {muscle}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {exercise.notes && (
                                <div className="mt-3 p-2 bg-muted/50 rounded text-sm">
                                  <span className="text-muted-foreground">Notes: </span>
                                  {exercise.notes}
                                </div>
                              )}
                            </Card>
                          ))}
                          <div className="mt-2 flex gap-2">
                            <Button 
                              onClick={() => handleStartWorkout(day)}
                              className="bg-gradient-primary text-black flex-1"
                              disabled={!!day.completed_at}
                            >
                              <Play className="h-4 w-4 mr-2" />
                              Start Gym Workout
                            </Button>
                            {!day.completed_at && (
                              <Button 
                                variant="outline"
                                onClick={() => handleDayComplete(day.id, day)}
                                disabled={completingDay === day.id}
                                className="border-green-500 text-green-500 hover:bg-green-500/10"
                              >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                {completingDay === day.id ? 'Marking...' : 'Done'}
                              </Button>
                            )}
                          </div>
                        </>
                      )}
                    </div>

                    {dayIndex < program.days.length - 1 && (
                      <Separator className="my-6" />
                    )}
                  </div>
                ))}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
